/**
 * Resume-ticket storage — REQ-A-020 / issue #880.
 *
 * The widget holds its session token in JavaScript memory only (PDPA /
 * M07-T05, `specs/widget-auth-contract.md` §3), so a real document load
 * ends the conversation. On a non-SPA entity site every link is a document
 * load: reproduced live on the DCCS vendor pages 2026-08-23, where the
 * patient also had to re-accept the Terms of Use on every page.
 *
 * What is stored here IS a credential, of deliberately narrow scope. It
 * cannot authenticate a send, a poll or a delete, and only one endpoint
 * accepts it — but that endpoint returns a session handle and the
 * transcript, so holding the ticket is as good as holding the session.
 * Calling it "not a credential" (as an earlier draft did) is wrong.
 *
 * What makes it defensible: single-use, superseded on every reply so only
 * ONE is ever live, bound to one session and one entity, dead once spent.
 * **The session token itself is still never persisted anywhere** — that
 * line of M07-T05 does not move.
 *
 * Residual: this runs on the ENTITY's origin, so any plugin or injected
 * script on their WordPress site can read this key.
 *
 * `sessionStorage` and never `localStorage`: it is per-tab and dies with
 * the tab, which is the shortest retention a browser offers (PDPA s.25).
 * A cookie would persist for its Max-Age across tab close and browser
 * restart — on a shared device that resumes a stranger's conversation.
 *
 * Every access is wrapped: private mode, disabled site data and
 * cross-origin sandboxing all make `sessionStorage` throw on ACCESS, not
 * just on write. A widget that throws here would fail to load at all, so
 * the degraded path is always "behave exactly as before this feature".
 */

const TICKET_PREFIX = "hana:rt:";
const PANEL_PREFIX = "hana:panel:";

export interface StoredTicket {
  value: string;
  mintedAt: number;
}

function safeGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* storage unavailable — resume simply won't be offered */
  }
}

function safeRemove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* nothing to do */
  }
}

/** Store the ticket for `entityId`, replacing any previous one. */
export function storeTicket(entityId: string, value: string): void {
  if (!entityId || !value) return;
  safeSet(
    TICKET_PREFIX + entityId,
    JSON.stringify({ value, mintedAt: Date.now() } satisfies StoredTicket),
  );
}

/** Read the stored ticket for `entityId`, or null. */
export function readTicket(entityId: string): string | null {
  if (!entityId) return null;
  const raw = safeGet(TICKET_PREFIX + entityId);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredTicket>;
    return typeof parsed?.value === "string" && parsed.value ? parsed.value : null;
  } catch {
    // Corrupt entry is indistinguishable from no entry, and keeping it
    // would retry forever. Drop it.
    safeRemove(TICKET_PREFIX + entityId);
    return null;
  }
}

/**
 * Drop the stored ticket.
 *
 * Called on EVERY exchange attempt — success or failure — before the
 * response is even inspected. A ticket is single-use server-side, so a
 * presented ticket is spent whatever the outcome; keeping it would make
 * the widget retry a dead value on the next navigation.
 */
export function clearTicket(entityId: string): void {
  if (!entityId) return;
  safeRemove(TICKET_PREFIX + entityId);
}

/** Remember whether the panel was open, so navigation restores it. */
export function storePanelState(entityId: string, open: boolean): void {
  if (!entityId) return;
  safeSet(PANEL_PREFIX + entityId, open ? "open" : "closed");
}

export function readPanelState(entityId: string): "open" | "closed" | null {
  if (!entityId) return null;
  const raw = safeGet(PANEL_PREFIX + entityId);
  return raw === "open" || raw === "closed" ? raw : null;
}

export function clearPanelState(entityId: string): void {
  if (!entityId) return;
  safeRemove(PANEL_PREFIX + entityId);
}
